import { useState, useEffect } from "react";

export const formatElapsed = (seconds: number): string => {
  if (seconds >= 3600) {
    return `${(seconds / 3600).toFixed(1)} hr`;
  } else if (seconds >= 60) {
    return `${(seconds / 60).toFixed(1)} min`;
  } else {
    return `${Math.round(seconds)} sec`;
  }
};

const useJobProgress = (props: {
  progressNumerator?: number;
  progressDenominator?: number;
  secondsElapsed?: number;
  progressStartDate?: string;
  startDate?: string;
  status?: string;
}) => {
  const {
    progressDenominator,
    progressNumerator,
    secondsElapsed: elapsed,
    status,
    progressStartDate,
    startDate,
  } = props;
  const secondsElapsed = elapsed ?? 0;

  const [now, setNow] = useState(Date.now());
  const [currentProgressNumerator, setCurrentProgressNumerator] =
    useState(progressNumerator);

  useEffect(() => {
    if (progressNumerator !== currentProgressNumerator) {
      setCurrentProgressNumerator(progressNumerator);
      setNow(Date.now());
    }
  }, [progressNumerator, currentProgressNumerator]);

  let progressText = "";
  let elapsedText = formatElapsed(secondsElapsed);
  const percentComplete =
    (progressNumerator ?? 0) / (progressDenominator || 100);
  const start = new Date(progressStartDate ?? startDate ?? "").valueOf();
  const elapsedMs = now - start;
  const totalEstimatedMs = elapsedMs / percentComplete;
  const remainingSeconds = (totalEstimatedMs - elapsedMs) / 1000;
  const remainingText =
    (progressNumerator ?? 0) > 0 && Number.isFinite(remainingSeconds)
      ? `Remaining: ${formatElapsed(Math.max(0, remainingSeconds))}`
      : "";
  if (status === "success") {
    progressText = elapsedText;
  } else if (status === "running") {
    progressText = `${Math.round(percentComplete * 100)}% ${
      remainingText ? `(${remainingText})` : ""
    }`;
  } else if (status === "error") {
    progressText = `${Math.round(percentComplete * 100)}% (${elapsedText})`;
  } else if (status === "retrying") {
    progressText = `${Math.round(
      percentComplete * 100
    )}% (${elapsedText}, retrying)`;
  }

  return {
    label: status === "running" ? "Progress" : "Execution Time",
    value: progressText,
  };
};

export default useJobProgress;
